import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { AccessibilityProvider } from "./contexts/AccessibilityContext";
import { Toaster } from "./components/ui/sonner";
import { VirtualAssistant } from "./components/ui/virtual-assistant";
import { WhatsAppButton } from "./components/ui/whatsapp-button";
import { AccessibilityPanel } from "./components/ui/accessibility-panel";
import { NewsletterPopup, useNewsletterPopup } from "./components/ui/newsletter-popup";
import "./i18n";
import "./App.css";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import Dashboard from "./pages/Dashboard";
import WarrantyForm from "./pages/WarrantyForm";
import WarrantyList from "./pages/WarrantyList";
import WarrantyDetail from "./pages/WarrantyDetail";
import InvoiceForm from "./pages/InvoiceForm";
import InvoiceList from "./pages/InvoiceList";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminWarranties from "./pages/admin/AdminWarranties";
import AdminInvoices from "./pages/admin/AdminInvoices";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminSEO from "./pages/admin/AdminSEO";
import AdminMarketing from "./pages/admin/AdminMarketing";
import TechnicianPanelPage from "./pages/admin/TechnicianPanelPage";
import GuidesPage from "./pages/GuidesPage";
import TradeInPage from "./pages/TradeInPage";
import ComparePage from "./pages/ComparePage";
import SEOLandingPage from "./pages/SEOLandingPage";
import { BrandPage, ModelPage } from "./pages/SEOModelPage";
import MiGarajePage from "./pages/MiGarajePage";
import ErrorCodesPage from "./pages/ErrorCodesPage";
import ModdingPage from "./pages/ModdingPage";
import CertificationPage from "./pages/CertificationPage";
import MaintenancePage from "./pages/MaintenancePage";
import PublicStatsPage from "./pages/PublicStatsPage";
import GoogleAuthCallback from "./pages/GoogleAuthCallback";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminCalendar from "./pages/admin/AdminCalendar";
import AdminEmailMarketing from "./pages/admin/AdminEmailMarketing";
import AdminRetention from "./pages/admin/AdminRetention";
import AdminClients from "./pages/admin/AdminClients";
import { AnalyticsTracker } from "./components/AnalyticsTracker";
import ProgrammaticSEOPage from "./pages/ProgrammaticSEOPage";
import FeedbackPage from "./pages/FeedbackPage";
import { SocialProofPopup } from "./components/SocialProof";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0047FF]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Auth Callback Component
const AuthCallback = () => {
  const { exchangeSession } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      exchangeSession(sessionId)
        .then((user) => {
          // Clear the hash
          window.history.replaceState(null, '', window.location.pathname);
          // Navigate based on role
          if (user.role === 'admin') {
            navigate('/admin', { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        })
        .catch((error) => {
          console.error('Session exchange failed:', error);
          navigate('/login', { replace: true });
        });
    } else {
      navigate('/login', { replace: true });
    }
  }, [exchangeSession, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0047FF]"></div>
    </div>
  );
};

// App Router with Virtual Assistant
function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/google" element={<GoogleAuthCallback />} />
        
        {/* Client Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/warranty/new" element={
          <ProtectedRoute>
            <WarrantyForm />
          </ProtectedRoute>
        } />
        <Route path="/warranty" element={
          <ProtectedRoute>
            <WarrantyList />
          </ProtectedRoute>
        } />
        <Route path="/warranty/:requestId" element={
          <ProtectedRoute>
            <WarrantyDetail />
          </ProtectedRoute>
        } />
        <Route path="/invoice/new" element={
          <ProtectedRoute>
            <InvoiceForm />
          </ProtectedRoute>
        } />
        <Route path="/invoice" element={
          <ProtectedRoute>
            <InvoiceList />
          </ProtectedRoute>
        } />

        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/warranties" element={
          <ProtectedRoute adminOnly>
            <AdminWarranties />
          </ProtectedRoute>
        } />
        <Route path="/admin/invoices" element={
          <ProtectedRoute adminOnly>
            <AdminInvoices />
          </ProtectedRoute>
        } />
        <Route path="/admin/settings" element={
          <ProtectedRoute adminOnly>
            <AdminSettings />
          </ProtectedRoute>
        } />
        <Route path="/admin/seo" element={
          <ProtectedRoute adminOnly>
            <AdminSEO />
          </ProtectedRoute>
        } />
        <Route path="/admin/marketing" element={
          <ProtectedRoute adminOnly>
            <AdminMarketing />
          </ProtectedRoute>
        } />
        <Route path="/admin/analytics" element={
          <ProtectedRoute adminOnly>
            <AdminAnalytics />
          </ProtectedRoute>
        } />
        <Route path="/admin/technician" element={
          <ProtectedRoute adminOnly>
            <TechnicianPanelPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/calendar" element={
          <ProtectedRoute adminOnly>
            <AdminCalendar />
          </ProtectedRoute>
        } />
        <Route path="/admin/email-marketing" element={
          <ProtectedRoute adminOnly>
            <AdminEmailMarketing />
          </ProtectedRoute>
        } />
        <Route path="/admin/retention" element={
          <ProtectedRoute adminOnly>
            <AdminRetention />
          </ProtectedRoute>
        } />
        <Route path="/admin/clients" element={
          <ProtectedRoute adminOnly>
            <AdminClients />
          </ProtectedRoute>
        } />

        {/* Public Gaming Pages */}
        <Route path="/guides" element={<GuidesPage />} />
        <Route path="/tradein" element={<TradeInPage />} />
        <Route path="/compare" element={<ComparePage />} />

        {/* Extended Features - Public */}
        <Route path="/errores" element={<ErrorCodesPage />} />
        <Route path="/modding" element={<ModdingPage />} />
        <Route path="/certificacion" element={<CertificationPage />} />
        <Route path="/mantenimiento" element={<MaintenancePage />} />
        <Route path="/estadisticas" element={<PublicStatsPage />} />

        {/* Extended Features - Protected */}
        <Route path="/mi-garaje" element={
          <ProtectedRoute>
            <MiGarajePage />
          </ProtectedRoute>
        } />

        {/* SEO Pages - City Landing Pages */}
        <Route path="/reparacion-en/:citySlug" element={<SEOLandingPage />} />
        
        {/* SEO Pages - Brand & Model Pages */}
        <Route path="/reparacion/:brandSlug" element={<BrandPage />} />
        <Route path="/reparacion/:brandSlug/:modelSlug" element={<ModelPage />} />
        
        {/* Programmatic SEO Pages - Full path with problem and city */}
        <Route path="/reparacion/:brand/:model/:problem/:city" element={<ProgrammaticSEOPage />} />

        {/* Feedback Page - Public (accessed via email link) */}
        <Route path="/feedback/:requestId" element={<FeedbackPage />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {/* Analytics Tracker - tracks page views */}
      <AnalyticsTracker />
      
      {/* WhatsApp Button - only visible on non-admin pages */}
      {!location.pathname.startsWith('/admin') && <WhatsAppButton />}
      
      {/* Accessibility Panel - always visible */}
      <AccessibilityPanel />
      
      {/* Virtual Assistant - show only for logged in non-admin users */}
      {user && user.role !== 'admin' && (
        <VirtualAssistant onNavigate={(path) => navigate(path)} />
      )}
      
      {/* Social Proof Popup - shows recent activity on non-admin pages */}
      {!location.pathname.startsWith('/admin') && <SocialProofPopup />}
    </>
  );
}

// App wrapper with newsletter popup
function AppWithNewsletter() {
  const { show, hidePopup } = useNewsletterPopup();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  return (
    <>
      <AppRouter />
      {!isAdminRoute && <NewsletterPopup show={show} onClose={hidePopup} />}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ThemeProvider>
          <AccessibilityProvider>
            <AuthProvider>
              <NotificationProvider>
                <AppWithNewsletter />
                <Toaster position="top-right" />
              </NotificationProvider>
            </AuthProvider>
          </AccessibilityProvider>
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
